// grade.ts - the deterministic Solo grader + end-of-hand report builder (S24). This is the training-
// wheels-off scoring: unlike Diagnose (which reveals a two-axis grade inline every street), Solo shows
// NOTHING until the hand is over, then grades all four streets at once on the READ (the tag), the PRICE
// (both typed numbers), and the PLAY (the action). Transcribed 1:1 from the mockup gradeSStreet +
// renderSReport (docs/ui-explorations/local-mode-read-first-v2.html lines 1376-1417).
//
// The report MODEL is the same <EndOfHandReport> contract Diagnose uses - Solo just builds its own model
// with Solo-voiced verdict copy and reuses report.ts's shared helpers (ACC/bandCounts/overallAccuracy/
// badgeFor). So the report component is reused verbatim.
import type { PlayerTag } from '../diagnose/data';
import type { MoveClass } from '../grade/types';
import {
  ACC,
  badgeFor,
  bandCounts,
  overallAccuracy,
  type ReportModel,
  type ReportStreet,
} from '../report/report';
import type { SoloHand, SoloStreetData } from './data';

/** One street's captured answer: the read (tag + weights), the typed price, and the chosen action. */
export interface SoloResult {
  tag: PlayerTag | null;
  weights: number[];
  action: 'fold' | 'call' | 'raise';
  /** the built raise amount (raw string), or '' for fold/call. */
  amount: string;
  /** the typed "to call" dollars (raw string), or '' when the street has no price. */
  callTyped: string;
  /** the typed required-equity %, or '' when the street has no price. */
  pctTyped: string;
}

/** A graded street before it becomes a report row: the move class, its accuracy, the play label, and a
 *  short sub explaining what (if anything) came off - the read, the price, or the whole play. */
export interface SoloStreetGrade {
  cls: MoveClass;
  acc: number;
  label: string;
  sub: string;
}

/** Digits only, as an integer (empty/garbage -> NaN). */
function intOf(s: string): number {
  return parseInt(String(s).replace(/[^0-9]/g, ''), 10);
}
/** A float (for the % - allows a decimal point). */
function floatOf(s: string): number {
  return parseFloat(String(s).replace(/[^0-9.]/g, ''));
}

/**
 * Grade one Solo street on the read, the price, and the play.
 *
 * A wrong action is graded straight to its branch's move class (the play is what matters most). A right
 * action is then penalised for a missed read and/or a missed price: the call must be EXACT and the % must
 * land within 3 points. No penalty -> Best; one slip -> Excellent; a bigger slip -> Inaccuracy. Mirrors
 * the mockup exactly (a wrong price number is a 2-point penalty, a slightly-off % is 1).
 */
export function gradeSoloStreet(
  result: SoloResult | null,
  street: SoloStreetData,
  correctTag: PlayerTag,
): SoloStreetGrade {
  if (!result) return { cls: 'inacc', acc: ACC.inacc, label: 'No answer', sub: '' };

  const actionRight = result.action === street.grade.correct;
  const readRight = result.tag === correctTag;

  // price: +1 for the exact call, +1 for a % within 3 points. null when the street has no price.
  let priceScore: number | null = null;
  if (street.price) {
    let ok = 0;
    if (intOf(result.callTyped) === street.price.call) ok++;
    const p = floatOf(result.pctTyped);
    if (!isNaN(p) && Math.abs(p - street.price.pct) <= 3) ok++;
    priceScore = ok;
  }

  if (!actionRight) {
    const w = street.grade.wrong[result.action] ?? { cls: 'mist' as MoveClass, label: result.action };
    return { cls: w.cls, acc: ACC[w.cls], label: w.label, sub: 'Wrong play' };
  }

  // right action - penalise a missed read / price.
  let pen = 0;
  if (!readRight) pen++;
  if (priceScore === 1) pen++;
  else if (priceScore === 0) pen += 2;
  const cls: MoveClass = pen === 0 ? 'best' : pen === 1 ? 'exc' : 'inacc';

  let sub: string;
  if (street.price) {
    sub = !readRight
      ? 'Read off'
      : priceScore === 2
        ? 'Read and price right'
        : priceScore === 1
          ? 'Price a little off'
          : 'Price missed';
  } else {
    sub = readRight ? 'Read right' : 'Read off';
  }

  return { cls, acc: ACC[cls], label: street.grade.rightLabel, sub };
}

/** Title-case a street id ("preflop" -> "Preflop"). */
function streetName(id: string): string {
  return id.charAt(0).toUpperCase() + id.slice(1);
}

/**
 * Build the Solo end-of-hand report from the four captured answers. Reuses the shared report helpers so
 * the accuracy line, band chips, and street rows render through the same <EndOfHandReport> as Diagnose -
 * only the verdict copy is Solo's own (derived from the real mix, never a fixed string).
 */
export function buildSoloReport(results: (SoloResult | null)[], hand: SoloHand): ReportModel {
  const streets: ReportStreet[] = hand.streets.map((s, i) => {
    const g = gradeSoloStreet(results[i] ?? null, s, hand.correctTag);
    return {
      name: streetName(s.id),
      acc: g.acc,
      cls: g.cls,
      action: g.label + (g.sub ? ' . ' + g.sub : ''),
    };
  });

  const accuracy = overallAccuracy(streets);
  const bands = bandCounts(streets);
  const allBest = streets.every((s) => s.cls === 'best');

  let verdict: string;
  let sub: string;
  if (allBest) {
    verdict = 'Clean read, start to finish';
    sub = 'You called the player and the price and played it straight through.';
  } else if (accuracy >= 90) {
    verdict = 'Sharp read, one street under';
    sub = 'One decision came in below your best, but the read carried the hand to the end.';
  } else if (accuracy >= 80) {
    verdict = 'Strong solo run';
    sub = 'A slip or two, but the read carried the hand.';
  } else if (accuracy >= 68) {
    verdict = 'Getting there';
    sub = 'The read or the price wobbled on a street. Watch where it dipped.';
  } else {
    verdict = 'Trust the read more';
    sub = 'A couple of streets got away from you. Replay the hand and read first.';
  }

  return {
    kicker: 'End of hand',
    h2: 'How you flew it',
    lede: 'Your solo run across all four streets. Graded on the read, the price, and the play, not on whether you won.',
    accuracy,
    badgeCls: badgeFor(accuracy),
    verdict,
    sub,
    streets,
    bands,
  };
}
